import React, { useCallback, useEffect, useEffectEvent, useMemo, useState } from 'react';
import { useCountdown, useStep } from 'usehooks-ts';
import { Check, Circle } from 'lucide-react';
import { Spinner } from '@/Components/shadcn';
import { cn } from '@/lib/utils';
import TMWingsAnimation from '@/Components/Animations/TMWingsAnimation/TMWingsAnimation';
import UIStrings from '@/Utils/UIStrings';

export interface Step {
    title: string;
    substeps: {
        title: string;
        /* in seconds */
        duration: number;
    }[];
}

interface MatchingProgressProps {
    readonly steps: Step[];
}

const MatchingProgress = ({ steps }: MatchingProgressProps) => {
    const totalStepsSubstep = useMemo(() => steps.map(({ substeps }) => substeps.length), [steps]);
    const [flatSteps, setFlatSteps] = useState(steps.flatMap((step) => step.substeps));
    const [currentStep, stepper] = useStep(steps.length);
    const [currentSubstep, substepStepper] = useStep(flatSteps.length);
    const [currentSubstepObject, setCurrentSubstepObject] = useState(flatSteps.find(({ duration }) => duration !== 0));
    const [count, control] = useCountdown({ countStart: currentSubstepObject?.duration || 0, intervalMs: 1000 });
    const [overallCount, overallCountControl] = useCountdown({
        countStart: flatSteps.reduce((prev, curr) => ({ ...prev, duration: prev.duration + curr.duration })).duration,
        intervalMs: 1000,
    });

    const init = useEffectEvent(() => {
        overallCountControl.startCountdown();
        flatSteps.forEach(({ duration }) => !duration && substepStepper.goToNextStep());
    });

    useEffect(() => {
        init();
    }, []);

    const stepForward = useEffectEvent(() => {
        const currentFlatStepIndex = flatSteps.findIndex(({ title }) => currentSubstepObject?.title === title);
        const nextStep = flatSteps[currentFlatStepIndex + 1];
        if (nextStep) {
            setCurrentSubstepObject(nextStep);
            setFlatSteps((p) => {
                // eslint-disable-next-line no-param-reassign
                p[currentFlatStepIndex].duration = 0;
                return p;
            });

            substepStepper.goToNextStep();
            control.resetCountdown();
        } else {
            control.stopCountdown();
        }
    });

    useEffect(() => {
        if (count === 0) {
            stepForward();
        }
    }, [count]);

    useEffect(() => {
        if (currentSubstepObject) {
            control.startCountdown();
        }
    }, [currentSubstepObject, control.startCountdown]);

    const getTotalStepsRemaining = useCallback(
        (target = currentStep) => totalStepsSubstep.slice(0, target).reduce((accum, curr) => accum + curr, 0),
        [currentStep, totalStepsSubstep],
    );

    useEffect(() => {
        if (getTotalStepsRemaining() < currentSubstep) {
            stepper.goToNextStep();
        }
    }, [stepper, currentSubstep, getTotalStepsRemaining]);

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col items-center gap-1">
                <TMWingsAnimation
                    width="60px"
                    height="60px"
                    aria-label="identify candidates with ai search animation"
                />
                <h2 className="text-2xl font-bold">{UIStrings.FindingTheBesMatchingCandidates}</h2>
                <span className="text-primary text-sm font-bold tracking-wider uppercase">
                    {UIStrings.StepNumberOfTotal(currentStep, steps.length)}
                    {' • '}
                    {overallCount ? UIStrings.MinuteElapsed(Math.ceil(overallCount / 60), true) : UIStrings.Finalizing}
                </span>
            </div>
            <div className="flex flex-col gap-6">
                {steps.map(({ substeps, title }, i) => (
                    <div
                        key={title}
                        className={cn(
                            'flex flex-col gap-2',
                            getTotalStepsRemaining(i) >= currentSubstep ? 'opacity-50' : 'opacity-100',
                        )}
                    >
                        <div className="flex items-center gap-2">
                            {i + 1 < currentStep ? (
                                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-500 text-white">
                                    <Check className="h-5 w-5" />
                                </div>
                            ) : (
                                <div
                                    className={cn(
                                        'bg-primary h-2 w-2 rounded-full',
                                        !(getTotalStepsRemaining(i) >= currentSubstep) && 'animate-pulse',
                                    )}
                                />
                            )}
                            <h4 className="text-lg font-bold">{title}</h4>
                        </div>
                        <div className="flex flex-col gap-2">
                            {substeps.map(({ title: substep }, j) => (
                                <div
                                    key={substep}
                                    className={cn(
                                        'ml-12 flex items-center',
                                        getTotalStepsRemaining(i) + j + 1 < currentSubstep
                                            ? 'text-green-500'
                                            : 'text-primary',
                                    )}
                                >
                                    <Circle className="mx-2 h-2 w-2 fill-current" />
                                    <span className="text-foreground text-sm">{substep}</span>
                                    <div className="ml-auto">
                                        {currentSubstepObject?.title === substep ? (
                                            <Spinner className="h-3.5 w-3.5" />
                                        ) : (
                                            <Check className="h-3.5 w-3.5" />
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default MatchingProgress;
